import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash2, GraduationCap, CheckCircle, AlertCircle, Clock, Download, Mail } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import API from '@/config/api';

const TRAINING_TYPES = [
  { value: 'online', label: 'Online Course' },
  { value: 'in_person', label: 'In-Person Workshop' },
  { value: 'conference', label: 'Conference' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'self_study', label: 'Self Study' },
  { value: 'other', label: 'Other' },
];

/**
 * Continuing-education hours tracker for one staff member.
 * DCF 251.05(3) requires 15 hours/year.
 */
const StaffCeHours = ({ staffId, staffName, readOnly = false }) => {
  const { getAuthHeaders } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState({ entries: [], total_hours: 0, annual_requirement: 15, remaining_hours: 15 });
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date_completed: new Date().toISOString().split('T')[0],
    topic: '',
    hours: '',
    training_type: 'online',
    provider_name: '',
    certificate_url: '',
    notes: '',
  });

  useEffect(() => { if (staffId) fetchEntries(); /* eslint-disable-line */ }, [staffId, year]);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/staff/${staffId}/ce-hours?year=${year}`, getAuthHeaders());
      setData(res.data);
    } catch {
      toast.error('Failed to load CE hours');
    }
  };

  const saveEntry = async () => {
    if (!form.topic || !form.hours || !form.date_completed) {
      toast.error('Date, topic, and hours are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/staff/${staffId}/ce-hours`, {
        ...form,
        hours: parseFloat(form.hours),
      }, getAuthHeaders());
      toast.success('CE entry logged');
      setShowAdd(false);
      setForm({
        date_completed: new Date().toISOString().split('T')[0],
        topic: '',
        hours: '',
        training_type: 'online',
        provider_name: '',
        certificate_url: '',
        notes: '',
      });
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save CE entry');
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this CE entry?')) return;
    try {
      await axios.delete(`${API}/continuing-education/${id}`, getAuthHeaders());
      toast.success('Entry deleted');
      fetchEntries();
    } catch {
      toast.error('Failed to delete entry');
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/staff/${staffId}/ce-hours/pdf?year=${year}`, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CE_Log_${(staffName || 'staff').replace(/\s+/g, '_')}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CE log downloaded');
    } catch {
      toast.error('Failed to download log');
    }
  };

  const emailLogToStaff = async () => {
    const note = window.prompt(
      `Email the ${year} CE log to ${staffName}?\n\nOptional message (leave blank to use default):`,
      ''
    );
    if (note === null) return; // cancelled
    try {
      const res = await axios.post(
        `${API}/staff/${staffId}/ce-hours/email`,
        { year, message: note || '' },
        getAuthHeaders()
      );
      toast.success(`${res.data.delivered ? 'Email sent' : 'Email queued'} to ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    }
  };

  const percent = Math.min(100, (data.total_hours / data.annual_requirement) * 100);
  const isComplete = data.total_hours >= data.annual_requirement;
  const urgencyColor = isComplete
    ? 'bg-green-500'
    : percent >= 70 ? 'bg-blue-500'
    : percent >= 40 ? 'bg-amber-500'
    : 'bg-red-500';

  return (
    <Card className="border-l-4 border-l-indigo-500" data-testid="staff-ce-hours">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-base">Continuing Education Hours</CardTitle>
              <p className="text-xs text-muted-foreground">DCF 251.05(3) — 15 hours/year required</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-24 h-8" data-testid="ce-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!readOnly && (
              <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="download-ce-pdf-btn">
                <Download className="w-3 h-3 mr-1" />
                PDF
              </Button>
            )}
            {!readOnly && (
              <Button size="sm" variant="outline" onClick={emailLogToStaff} data-testid="email-ce-log-btn">
                <Mail className="w-3 h-3 mr-1" />
                Email
              </Button>
            )}
            {!readOnly && (
              <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-ce-hours-btn">
                <Plus className="w-3 h-3 mr-1" />
                Log Hours
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">
              {data.total_hours.toFixed(1)} / {data.annual_requirement} hours
            </span>
            {isComplete ? (
              <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Complete</Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> {data.remaining_hours?.toFixed(1)} hrs remaining</Badge>
            )}
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={cn('h-full transition-all', urgencyColor)}
              style={{ width: `${percent}%` }}
              data-testid="ce-progress-bar"
            />
          </div>
        </div>

        {/* Entries */}
        {data.entries.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No CE entries logged for {year}. {!readOnly && 'Click "Log Hours" to add one.'}
          </p>
        ) : (
          <div className="space-y-2">
            {data.entries.map(e => (
              <div key={e.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg" data-testid={`ce-entry-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{e.topic}</span>
                    <Badge variant="outline" className="text-[10px]">{e.hours} hrs</Badge>
                    {e.training_type && <Badge variant="secondary" className="text-[10px]">{TRAINING_TYPES.find(t => t.value === e.training_type)?.label || e.training_type}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(e.date_completed + 'T12:00:00').toLocaleDateString()}
                    {e.provider_name && ` • ${e.provider_name}`}
                    {e.notes && ` • ${e.notes}`}
                  </p>
                </div>
                {!readOnly && (
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteEntry(e.id)} data-testid={`delete-ce-${e.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg" data-testid="log-ce-dialog">
          <DialogHeader>
            <DialogTitle>Log Continuing Education Hours</DialogTitle>
            <DialogDescription>{staffName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date Completed</Label>
                <Input type="date" value={form.date_completed} onChange={(e) => setForm({ ...form, date_completed: e.target.value })} data-testid="ce-date-input" />
              </div>
              <div>
                <Label className="text-xs">Hours</Label>
                <Input type="number" min="0" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="e.g. 2.5" data-testid="ce-hours-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Topic / Training Title</Label>
              <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="e.g. Child Development: Emotional Regulation" data-testid="ce-topic-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Training Type</Label>
                <Select value={form.training_type} onValueChange={(v) => setForm({ ...form, training_type: v })}>
                  <SelectTrigger data-testid="ce-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRAINING_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Provider</Label>
                <Input value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} placeholder="e.g. WI Early Childhood Association" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Certificate URL (optional)</Label>
              <Input value={form.certificate_url} onChange={(e) => setForm({ ...form, certificate_url: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button onClick={saveEntry} disabled={saving} data-testid="save-ce-btn">
                {saving ? 'Saving…' : 'Save Entry'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default StaffCeHours;
